import { useCallback, useMemo } from 'react';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';

import * as errorUI from '../../ui/error';
import { getOTErrorTranslationKey } from '../helpers/utils';
import { isLoggedUserSelector } from '../../context/selectors';
import { virtualVisitActions } from '../../healthboard/virtual-visit';
import { videoCallEndPopupActions } from '../../ui/videoCallEndPopup';
import { selectCurrentAppointment } from '../../appointmnents/selectors';
import * as appointmentDetails from '../../appointmnents/appointment-details-req';
import {
  APPOINTMENT_STATUSES,
  URGENT_VISIT_TYPE_CODE,
  VIRTUAL_VISIT_TYPE_CODE,
} from '../../../constants';
import {
  selectVirtualVisitConfig,
  selectVirtualVisitOpened,
  selectVirtualVisitDragging,
  selectVirtualVisitCollapsed,
  selectVirtualVisitFullScreenView,
  selectRoomInvitationPatientProfile,
  selectRoomInvitationInvitee,
  selectRoomInvitationRole,
  selectVirtualVisitMobSidebarOpened,
} from '../../healthboard/selectors';
import { CARE_ROLE } from '../../../constants/props';

const { NO_SHOW, CANCELED, COMPLETED, WAITING } = APPOINTMENT_STATUSES;
const endVisitStatuses = [NO_SHOW, CANCELED, COMPLETED];

export function useVirtualVisit() {
  const patientProfile = useSelector(selectRoomInvitationPatientProfile);
  const roomInvitationInvitee = useSelector(selectRoomInvitationInvitee);
  const roomInvitationRole = useSelector(selectRoomInvitationRole);

  const dispatch = useDispatch();
  const config = useSelector(selectVirtualVisitConfig);
  const opened = useSelector(selectVirtualVisitOpened);
  const isOpenedMobSidebar = useSelector(selectVirtualVisitMobSidebarOpened);
  const dragging = useSelector(selectVirtualVisitDragging);
  const appointment = useSelector(selectCurrentAppointment);
  const isLoggedUser = useSelector(isLoggedUserSelector);

  const loading = false;
  const userId = get(appointment, 'userId');
  const appointmentId = get(appointment, 'id');

  const collapsed = useSelector(selectVirtualVisitCollapsed);
  const fullScreenView = useSelector(selectVirtualVisitFullScreenView);

  const otConfig = useMemo(() => {
    if (!config) return null;
    return {
      sessionID: config.room,
      token: config.token,
      apiKey: config.apiKey,
    };
  }, [config]);

  const initialRoomPendingInvitations = useMemo(() => {
    const fromMeta = get(config, 'roomPendingInvitations');
    return Array.isArray(fromMeta) ? fromMeta : [];
  }, [config]);

  const roomMeta = useMemo(
    () => ({
      name: get(config, 'roomName', ''),
      number: get(config, 'roomNumber', '-'),
      connectionRoom: get(config, 'connectionRoom', false),
      type: get(config, 'type'),
      visitType: get(config, 'visitTypeName', ''),
    }),
    [config],
  );

  const cancelCallInvitation = useCallback(
    email => {
      console.log('cancel invite', email);
    },
    [appointmentId],
  );

  const openCallSettings = useCallback(() => {
    console.log('open call settings');
  }, []);

  const expandView = useCallback(
    () => dispatch(virtualVisitActions.setCollapsing(false)),
    [],
  );
  const collapseView = useCallback(() => {
    dispatch(virtualVisitActions.setFullScreenView(false));
    dispatch(virtualVisitActions.setCollapsing(true));
  }, []);

  const destroyVisit = useCallback(
    () => dispatch(virtualVisitActions.reset()),
    [],
  );

  const toggleFullScreenView = useCallback(
    () => dispatch(virtualVisitActions.setFullScreenView(!fullScreenView)),
    [fullScreenView],
  );

  const publisherUserData = useMemo(() => {
    if (roomInvitationRole === CARE_ROLE.GUARDIAN && roomInvitationInvitee) {
      return {
        avatar: {},
        name: `${roomInvitationInvitee.firstName} ${roomInvitationInvitee.lastName}`,
        id: roomInvitationInvitee.id,
        isProvider: false,
      };
    }

    if (patientProfile) {
      return {
        avatar: {},
        name: `${patientProfile.firstName} ${patientProfile.lastName}`,
        id: patientProfile.id,
        isProvider: false,
      };
    }

    return {
      avatar: get(appointment, 'patientUserInfo.avatar.thumbMin', ''),
      name: get(appointment, 'patientUserInfo.displayableName', ''),
      id: get(appointment, 'patientUserInfo.id'),
      isProvider: false,
    };
  }, [appointment, patientProfile, roomInvitationInvitee, roomInvitationRole]);

  const displayErrorModal = useCallback(name => {
    const { description } = getOTErrorTranslationKey(name);
    dispatch(errorUI.actions.update({ id: description }));
  }, []);

  const handleGlobalError = useCallback(name => {
    destroyVisit();
    displayErrorModal(name);
  }, []);

  const toggleMobSidebar = useCallback(() => {
    dispatch(virtualVisitActions.toggleMobSidebar());
  }, []);

  const placeholder = useMemo(() => {
    const code = get(config, 'typeCode');
    const status = get(appointment, 'status.coreAppName');

    if (code === URGENT_VISIT_TYPE_CODE && status === WAITING) {
      return {
        title: 'virtualVisit.placeholder.title',
        subtitle: 'virtualVisit.placeholder.subtitle',
        ...(get(appointment, 'copay') && {
          extraSubtitle: 'appointment.payment.charged',
        }),
      };
    }

    if (code === VIRTUAL_VISIT_TYPE_CODE) {
      return {
        title: 'virtualVisit.regural.placeholder.title',
        subtitle: 'virtualVisit.regural.placeholder.subtitle',
      };
    }
  }, [config, appointment]);

  const openConfirmationPopup = status => {
    if (endVisitStatuses.includes(status)) {
      dispatch(videoCallEndPopupActions.open(status));
    }
  };

  const updateAppointment = () => {
    if (isLoggedUser)
      dispatch(appointmentDetails.actions.send({ userId, appointmentId }));
  };

  return {
    viewOptions: {
      opened,
      dragging,
      collapsed,
      expandView,
      collapseView,
      fullScreenView,
      toggleMobSidebar,
      toggleFullScreenView,
      mobSidebarOpened: isOpenedMobSidebar,
    },
    callState: {
      otConfig,
      loading,
      roomMeta,
      destroyVisit,
      initialRoomPendingInvitations,
    },
    appointment,
    placeholder,
    publisherUserData,
    cancelCallInvitation,
    openCallSettings,
    displayErrorModal,
    handleGlobalError,
    openConfirmationPopup,
    updateAppointment,
  };
}



// WEBPACK FOOTER //
// ./src/redux/opentok/hooks/useVirtualVisit.js